/**
 * The staff line for a failed card save (fix-plain-card-errors).
 *
 * Saving a card goes to pay-api's vault. When Square refuses it, pay-api answers
 * `422 VAULT_FAILED` with "Square rejected the card step: <SQUARE_CODE>", and dink-api passes
 * that through as-is. Staff never see that sentence or the code:
 *  - a known reason (dink-api's `declineReason`, or the Square code in the message mapped
 *    through dink-api's table) reads its plain line from `./declineReason`;
 *  - one of pay-api's actionable vault sentences reads its own plain line
 *    (`knownVaultSentenceLine`);
 *  - any other vault failure or provider text reads {@link CARD_SAVE_FAILED_MESSAGE}.
 *
 * Every other failure keeps the surface's existing copy: dink's own message through the
 * `serverMessage` seam, else the caller's fallback.
 */
import { ApiError } from '@/api/errors';
import { serverMessage } from '@/api/serverMessage';
import { v2ErrorCode } from '@/api/v2Errors';
import {
  declineReasonLineFromError,
  isProviderMessage,
  knownVaultSentenceLine,
} from '@/utils/declineReason';

/** The line for a card save that failed with no reason staff can act on. */
export const CARD_SAVE_FAILED_MESSAGE = "The card couldn't be saved. Try again or use another card.";

/** pay-api's definitive card vault refusal, passed through by dink-api (DNKAPI-0349). */
const VAULT_FAILED = 'VAULT_FAILED';

/** The server message on an `ApiError`, raw (only ever matched here, never rendered). */
function rawMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const message = error.details?.['message'];
  return typeof message === 'string' ? message : null;
}

/** Whether a failure is pay-api's card vault refusal (`VAULT_FAILED`). */
export function isCardVaultFailure(error: unknown): boolean {
  return error instanceof ApiError && v2ErrorCode(error) === VAULT_FAILED;
}

/**
 * The plain line for a vault refusal, or null when the failure is not one. For the sale
 * dialogs, which save the card before they charge it and map every other failure their own way.
 */
export function cardVaultFailureMessage(error: unknown): string | null {
  if (!isCardVaultFailure(error)) return null;
  return (
    declineReasonLineFromError(error) ??
    knownVaultSentenceLine(rawMessage(error)) ??
    CARD_SAVE_FAILED_MESSAGE
  );
}

/**
 * The staff line for a failed card save: a known reason's line; a known vault sentence's
 * line; the plain save line for any other vault refusal or provider text; otherwise dink's
 * own message, else `fallback`.
 */
export function cardSaveErrorMessage(
  error: unknown,
  fallback: string = CARD_SAVE_FAILED_MESSAGE,
): string {
  const reasonLine = declineReasonLineFromError(error);
  if (reasonLine !== null) return reasonLine;
  const message = rawMessage(error);
  const vaultLine = knownVaultSentenceLine(message);
  if (vaultLine !== null) return vaultLine;
  if (isCardVaultFailure(error) || isProviderMessage(message)) return CARD_SAVE_FAILED_MESSAGE;
  return serverMessage(error) ?? fallback;
}
